import { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { isDefined } from '@e-socna/utils';

import { graphql } from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';
import type { Id } from '@e-socna/data-layer';

import { Breadcrumbs } from '../../components/Breadcrumbs';
import {
  FileExplorer,
  FileExplorerStrategy,
  useFilesPageState,
} from '../../components/FileExplorer';
import { Loading } from '../../components/Loading';
import {
  PATH_FOLDER_DEFAULT,
  PATH_FOLDER_FILES_DEFAULT,
  PATH_FOLDERS_DEFAULT,
} from '../../app/constants/paths.constant';
import { useFolderContext } from '../../contexts/FolderContext/hooks/useFolderContext.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import type { Mode } from '../../components/FileExplorer/types/mode.type';

import { FolderPage_OneFolder_QueryDocument } from '../Folder/hooks/useFindOneFolderQuery.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { TUS_CATEGORY_FOLDER_FILES } from './constants/tus.constant';

const FolderFilesPage_FileDirectory_QueryDocument = graphql(`
  query FolderFilesPage_FileDirectory_Query($folderId: Id!) {
    findOneFolder(id: $folderId) {
      _id
      fileDirectory {
        _id
        allowedFileCategories {
          _id
          hideInFileExplorer
          label
          parentCategoryId
        }
        fileCategoryAccesses {
          ...FileExplorer_FileCategoryAccess_Fragment
        }
        files {
          _id
          categoryId
          originalName
          mimeType
          size
          uri
          compressedVariantId
          thumbnailVariantId
          hideInFileExplorer
        }
      }
    }
  }
`);

const FolderFilesPage_DeleteFiles_MutationDocument = graphql(`
  mutation FolderFilesPage_DeleteFiles_Mutation(
    $entityId: Id!
    $fileIds: [Id!]!
  ) {
    deleteFolderFiles(entityId: $entityId, fileIds: $fileIds)
  }
`);

const FolderFilesPage_MoveFiles_MutationDocument = graphql(`
  mutation FolderFilesPage_MoveFiles_Mutation(
    $entityId: Id!
    $destinationFileCategoryId: Id!
    $fileIds: [Id!]!
  ) {
    moveFolderFiles(
      entityId: $entityId
      destinationFileCategoryId: $destinationFileCategoryId
      fileIds: $fileIds
    )
  }
`);

const FolderFilesPage_RenameFile_MutationDocument = graphql(`
  mutation FolderFilesPage_RenameFile_Mutation(
    $entityId: Id!
    $fileId: Id!
    $newOriginalName: NonEmptyString!
  ) {
    renameFolderFile(
      entityId: $entityId
      fileId: $fileId
      newOriginalName: $newOriginalName
    )
  }
`);

export const FolderFilesPage = () => {
  const context = useFolderContext();

  const { paths } = useBuildPaths(
    { default: PATH_FOLDER_DEFAULT, files: PATH_FOLDER_FILES_DEFAULT },
    { folderId: context.folderId },
  );

  // Must be defined before any conditional return (hooks rule).
  const buildFilesPath = useCallback(
    (fileCategoryId: Id, mode: Mode, fileId?: Id | null): string => {
      const base = `${paths.files}/${String(fileCategoryId)}/${mode}`;
      return isDefined(fileId) ? `${base}/${String(fileId)}` : base;
    },
    [paths.files],
  );

  const {
    allowedFileCategories,
    fileCategoryAccesses,
    files,
    fileCountsByCategoryId,
    isLoadingFileDirectory,
    isDeletingFiles,
    isMovingFiles,
    isRenamingFile,
    queryDocumentNamesToInvalidate,
    handleDeleteFiles,
    handleMoveFiles,
    handleRenameFile,
  } = useFilesPageState({
    entityId: context.folderId,
    buildFilesPath,
    queryDocument: FolderFilesPage_FileDirectory_QueryDocument,
    queryVariables: { folderId: context.folderId ?? EMPTY_ID },
    selectFileDirectory: (data) => data.findOneFolder.fileDirectory,
    deleteFilesMutationDocument: FolderFilesPage_DeleteFiles_MutationDocument,
    moveFilesMutationDocument: FolderFilesPage_MoveFiles_MutationDocument,
    renameFileMutationDocument: FolderFilesPage_RenameFile_MutationDocument,
    relatedQueryDocumentsToInvalidate: [FolderPage_OneFolder_QueryDocument],
  });

  const breadcrumbs = (
    <>
      <Helmet>
        <title>
          {buildDocumentTitle(
            'Liste des Fichiers du Dossier',
            context.folder?.code,
          )}
        </title>
      </Helmet>
      <Breadcrumbs
        paths={[
          { key: 'folders', label: 'dossiers', to: PATH_FOLDERS_DEFAULT },
          { key: 'folder', label: 'dossier', to: paths.default },
          { key: 'files', label: 'fichiers', to: paths.files },
        ]}
      />
    </>
  );

  if (!context.folder) {
    return (
      <>
        <Loading />
        {breadcrumbs}
        <PageTitle title="Liste des Fichiers du Dossier" />
      </>
    );
  }

  return (
    <>
      {breadcrumbs}
      <PageTitle
        code={context.folder.code}
        title={context.folder.consolidation.title}
      />
      <FileExplorer
        allowedFileCategories={allowedFileCategories}
        buildPath={buildFilesPath}
        entityId={context.folderId}
        entityLabel={`Dossier ${context.folder.code}`}
        fileCategoryAccesses={fileCategoryAccesses}
        fileCountsByCategoryId={fileCountsByCategoryId}
        files={files}
        filesQueryDocumentNamesToInvalidate={queryDocumentNamesToInvalidate}
        isDeletingFiles={isDeletingFiles}
        isLoadingCategories={isLoadingFileDirectory}
        isLoadingFiles={isLoadingFileDirectory}
        isMovingFiles={isMovingFiles}
        isRenamingFile={isRenamingFile}
        onDeleteFiles={handleDeleteFiles}
        onMoveFiles={handleMoveFiles}
        onRenameFile={handleRenameFile}
        strategy={FileExplorerStrategy.RouterConnected}
        tusCategory={TUS_CATEGORY_FOLDER_FILES}
      />
    </>
  );
};
